// @flow

import * as React from 'react';
import includes from 'lodash/fp/includes';
import FileUploadTemplate from '@active/awe-ui-core/lib/components/FileUpload/FileUploadTemplate';
import type { FileUploadResponse } from 'type/type';

import './FileUpload.scss';

const prefix = process.env.REACT_APP_API === 'mock' ? '/mock' : '';
const FILE_TOKEN_URL = `${prefix}/api/file/metadata`;
const UPLOAD_FILE_URL = `${prefix}/api/file`;
const FILE_STATUS_URL = `${prefix}/api/file`;

const INIT = 'FILE_UPLOAD_INIT';
const UPLOADING = 'FILE_UPLOAD_ING';
const FAILED = 'FILE_UPLOAD_FAILED';
const SUCCESS = 'FILE_UPLOAD_SUCCESS';
const FILE_TOO_LARGE = 'FileSizeError';
const FILE_TYPE_ERROR = 'FileTypeError';

type State = {
  uploadStatus: string,
  message: string,
}

type Error = {
  name: string,
}

type ErrorResponse = Object

type UploadMessages = {
  uploadFailed: string,
  uploading: string,
  fileTooLarge: string,
  fileTypeError: string,
}

type Props = {
  placeholder?: string,
  requestPayload?: Object,
  acceptType: string,
  buttonName: string,
  fileMaxSize: number,
  uploadMessages: UploadMessages,
  disabled?: boolean,
  onStartCallBack?: () => void,
  onSuccessCallBack?: (FileUploadResponse) => void,
  onFailedCallBack?: (ErrorResponse) => void,
  tips?: React.Node,
}

class FileUpload extends React.Component<Props, State> {
  state = { uploadStatus: INIT, message: '' }

  onSuccess = (response: FileUploadResponse) => {
    this.setState({ uploadStatus: SUCCESS });
    const { onSuccessCallBack } = this.props;
    onSuccessCallBack && onSuccessCallBack(response);
  }

  getErrorMessage = ({ name }: Error) => {
    const {
      uploadMessages: { fileTooLarge, uploadFailed, fileTypeError },
    } = this.props;
    if (name === FILE_TOO_LARGE) {
      return fileTooLarge;
    }

    if (name === FILE_TYPE_ERROR) {
      return fileTypeError;
    }

    return uploadFailed;
  }

  onFailed = (error: Error) => {
    const { onFailedCallBack } = this.props;
    const message = this.getErrorMessage(error);

    this.setState({ message, uploadStatus: FAILED });
    onFailedCallBack && onFailedCallBack(error);
  }

  onStart = () => {
    const { uploadMessages: { uploading } } = this.props;
    this.setState({ uploadStatus: UPLOADING, message: uploading });
    const { onStartCallBack } = this.props;
    onStartCallBack && onStartCallBack();
  }

  renderMessage = () => {
    const { uploadStatus, message } = this.state;
    return includes(uploadStatus)([ UPLOADING, FAILED ]) ? (
      <span className={`file-upload__message--${uploadStatus === UPLOADING ? 'loading' : 'error'}`}>
        {uploadStatus === FAILED && <i className="icon-times-circle" />}
        {message}
      </span>
    ) : null;
  }

  render() {
    const {
      buttonName,
      acceptType,
      placeholder,
      requestPayload = {},
      fileMaxSize,
      disabled,
      tips,
    } = this.props;

    return (
      <React.Fragment>
        <div className="file_upload__container">
          <FileUploadTemplate
            placeHolder={placeholder}
            requestUrls={{
              getFileTokenUrl: FILE_TOKEN_URL,
              uploadFileUrl: UPLOAD_FILE_URL,
              getFileStatusUrl: FILE_STATUS_URL,
            }}
            requestPayload={requestPayload}
            onSuccess={this.onSuccess}
            onFailed={this.onFailed}
            onStart={this.onStart}
            buttonName={buttonName}
            acceptType={acceptType}
            fileMaxSize={fileMaxSize}
            disabled={disabled} />
        </div>
        {tips}
        {this.renderMessage()}
      </React.Fragment>
    );
  }
}

export default FileUpload;
